import {
  Avatar,
  CardActionArea,
  Divider,
  Drawer,
  Grid,
  Menu,
  MenuItem,
  Popover,
  Stack,
  styled,
  Typography
} from '@mui/material';
import {useNavigate} from 'react-router-dom';

import {Sizes} from '@constants/sizes.constants';

import {Colors} from '@constants/colors.constants';
import {AdminRoutes, SettingsRoutes, useRouteMatch} from '@utils/routes.utils';
import React, {useState} from 'react';
import {LanguageOutlined, LogoutOutlined, SettingsOutlined} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {useObservable} from '@ngneat/react-rxjs';
import {sessionQuery, sessionService} from '@store/session';
import {usePopup} from '@components/modal/popup/PopupService';
import ModalComponent from '@components/modal/Modal.component';
import {Locales} from '@constants/locales.constants';
import SelectComponent from '@components/inputs/select/Select.component';
import {getCurrentLanguage} from '@utils/i18n.utils';

const Logo = styled('img')`
    object-fit: contain;
    width: 47px;
`;

const MainDrawer = styled(Drawer)(() => ({
  transition: 'width 0.3s ease',
  '& .MuiDrawer-paper': {
    transition: 'width 0.3s ease',
  }
}));

const LeftBarComponent = () => {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();
  const currentRoute = useRouteMatch([...AdminRoutes.routes(), AdminRoutes.SETTINGS]);
  const {i18n, t} = useTranslation();
  const [anchorSettings, setAnchorSettings] = useState(null);
  const [anchorAccount, setAnchorAccount] = useState(null);
  const [{currentUser}] = useObservable(sessionQuery.currentUser$);
  const [openUpdateLanguage, setOpenUpdateLanguage] = useState(false);
  const popConfirm = usePopup();
  const currentLanguage = getCurrentLanguage();

  const handleAccountClick = (event) => {
    setAnchorAccount(event.currentTarget);
  };

  const handleAccountClose = () => {
    setAnchorAccount(null);
    setHovered(false);
  };

  const handleSettingsClick = (event) => {
    setAnchorSettings(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setAnchorSettings(null);
    setHovered(false);
  };

  const changeLanguage = (newValue: string) => {
    i18n.changeLanguage(newValue);
  };

  const handleLogout = () => {
    popConfirm({
      title: t('logout.title'),
      description: t('logout.description'),
      buttons: [
        {
          label: t('logout.button'),
          color: 'error',
          onClick: sessionService.logout,
        },
      ],
    });
  };

  const openSettings = Boolean(anchorSettings);
  const openAccount = Boolean(anchorAccount);

  const SettingsRouteLink = styled(CardActionArea)`
      background: ${Colors.background};
      height: 118px;
      width: 160px;
  `;

  const expandMenu = hovered || openSettings || openAccount;

  return (
    <MainDrawer open variant="persistent" anchor="left" hideBackdrop
                sx={{
                  width: expandMenu ? '200px' : Sizes.leftBar,
                  zIndex: expandMenu ? 1400 : 'initial',
                  '& .MuiDrawer-paper': {
                    width: expandMenu ? '200px' : Sizes.leftBar,
                    borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                    zIndex: expandMenu ? 1400 : 'initial',
                  }
                }}
    >
      <Stack direction="column" alignItems="center" height="100%" position="relative" spacing={2}
             onMouseEnter={() => {
               setHovered(true);
             }}
             onMouseLeave={() => setHovered(false)}
             sx={{width: expandMenu ? '200px' : Sizes.leftBar}}>
        <Stack width="100%" direction="row" height={`${Sizes.topBar}px`}
               sx={{minHeight: `${Sizes.topBar}px`, paddingLeft: '12px'}}>
          <Logo alt="metawaste logo" src="/images/metawaste-logo.png"/>
        </Stack>
        <Stack flex={1} justifyContent="center" spacing={2} width="100%">
          {AdminRoutes.routes().map((route) => {
            const RouteLink = styled(CardActionArea)`
                background: ${currentRoute === route ? `linear-gradient(to right, ${Colors.primary} 0, ${Colors.primary} 5px, transparent 4px)` : 'none'};
                color: ${Colors.text};
                height: 53px;
                text-decoration: none;
                width: 100%;
            `;

            const Icon = () => AdminRoutes.Icon(route, currentRoute === route);

            return (
              <RouteLink key={route} onClick={() => navigate(route)}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{paddingLeft: '25px'}}>
                  <Icon/>
                  {expandMenu && <Typography fontSize={12} fontWeight="400" textAlign="center"
                                             color={currentRoute === route ? 'primary' : undefined}>
                    {AdminRoutes.label(route)}
                  </Typography>}
                </Stack>
              </RouteLink>
            );
          })}
          <CardActionArea key={AdminRoutes.SETTINGS} sx={{
            height: 53,
            background: currentRoute === AdminRoutes.SETTINGS ? `linear-gradient(to right, ${Colors.primary} 0, ${Colors.primary} 5px, transparent 4px)` : 'none'
          }} onClick={handleSettingsClick}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{paddingLeft: '25px'}}>
              <SettingsOutlined color={currentRoute === AdminRoutes.SETTINGS ? 'primary' : undefined}/>
              {expandMenu && <Typography fontSize={12} fontWeight="400" textAlign="center"
                                         color={currentRoute === AdminRoutes.SETTINGS ? 'primary' : undefined}>
                {t('routes.settings')}
              </Typography>}
            </Stack>
          </CardActionArea>
          <CardActionArea key="account" sx={{
            height: 53,
          }} onClick={handleAccountClick}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{paddingLeft: '21px'}}>
              <Avatar sx={{backgroundColor: Colors.text, height: 30, fontSize: 14, width: 30}}>
                {currentUser?.firstName?.[0] || ''}{currentUser?.lastName?.[0] || ''}
              </Avatar>
              {expandMenu && <Typography fontSize={12} fontWeight="400" textAlign="center">
                {[currentUser?.firstName, currentUser?.lastName].join(' ')}
              </Typography>}
            </Stack>
          </CardActionArea>
        </Stack>
      </Stack>
      <Menu open={openAccount} anchorEl={anchorAccount} onClose={handleAccountClose}
            onClick={handleAccountClose}>
        <MenuItem>
          <Stack direction="row" spacing={2} alignItems="center" onClick={() => setOpenUpdateLanguage(true)}>
            <LanguageOutlined/>
            <Typography>{t('account.updateLanguage')}</Typography>
          </Stack>
        </MenuItem>
        <Divider/>
        <MenuItem onClick={handleLogout}>
          <Stack direction="row" spacing={2} alignItems="center">
            <LogoutOutlined/>
            <Typography>{t('logout.title')}</Typography>
          </Stack>
        </MenuItem>
      </Menu>
      <Popover
        open={openSettings}
        onClose={handleSettingsClose}
        anchorEl={anchorSettings}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Grid container spacing={2} sx={{maxWidth: 710, padding: '20px'}}>
          {SettingsRoutes.routes().map((r) => {
            return (
              <Grid item key={r.path} xs={3}>
                <SettingsRouteLink onClick={() => {
                  navigate(`${AdminRoutes.SETTINGS}/${r.path}`);
                  handleSettingsClose();
                }}>
                  <Stack direction="column" alignItems="center" justifyContent="center">
                    <Typography fontWeight={700} sx={{textAlign: 'center'}}>{r.label}</Typography>
                  </Stack>
                </SettingsRouteLink>
              </Grid>
            );
          })}
        </Grid>
      </Popover>
      {openUpdateLanguage && <ModalComponent
          handleClose={() => setOpenUpdateLanguage(false)}
          title={t('account.updateLanguage')}
          maxWidth="sm"
          content={(
            <Stack pt="20px" pb="20px" width="100%" direction="column" alignItems="center" spacing={2}
                   overflow="hidden">
              <SelectComponent
                label={t('account.language')}
                items={Locales.selectItems()}
                color="secondary"
                inputWidth={200}
                value={currentLanguage}
                handleChange={changeLanguage}
              />
            </Stack>
          )}
      />}
    </MainDrawer>

  );
};

export default LeftBarComponent;
